import { mergeWorkoutLayers, mergeWorkoutLayersWithExternal, tagPrescriptionLayer, } from './merge-workout-layers';
/**
 * Stable key for indexing a {@link BowlingSummaryDay} by athlete + day.
 * Consumers (overlay attachment, conflict checks) must use this exact format
 * — the apps/web overlay layer builds the same key from `ActualWorkout` and
 * prescription rows to look up cross-block overlap.
 */
export function bowlingSummaryDayKey(profileId, assignedDate) {
    return `${profileId}|${assignedDate}`;
}
// The single source of truth for actuals source ranking. Keep in sync with
// ACTUALS_SOURCE_RANKING in apps/web actuals-workouts.ts. 'Prescription'
// is a merge-only sentinel applied via tagPrescriptionLayer so prescribed
// setMetrics lose to every actual-layer source, including Manual Override.
// The AMS sync (mergeAmsActualWorkout in trpc-server) consumes this exported
// ranking so synced values match the planner actuals (SC-5468).
export const BOWLING_SOURCE_RANKING = [
    'Manual Override',
    'Catapult',
    'Player Entered',
    null,
    'Match Stats',
    'Prescription',
];
/**
 * Identity key for collapsing rows that describe the same source workout
 * (block + workout + athlete + day). The overlap query can emit the same
 * tuple via both the published and draft branches; dedup keeps only the
 * first occurrence to honor the "published wins" rule callers expect.
 */
function dedupRowKey(row) {
    return `${row.source.blockId}|${row.source.workoutId}|${row.profileId}|${row.assignedDate}`;
}
/**
 * Shapes a {@link WorkoutLayer} into the `HasWorkoutLayer` input expected by
 * the merge helpers, or returns `undefined` when the layer is absent so the
 * slot can be passed straight through to the variadic merge. The synthetic
 * `id` is per-layer-kind so the merger doesn't conflate the block, athlete,
 * and actual layers from the same source workout.
 */
function layerInput(row, kind, layer) {
    if (!layer)
        return undefined;
    return {
        id: `${row.source.blockId}|${row.source.workoutId}|${kind}`,
        workoutId: row.source.workoutId,
        layer,
        assignedDate: row.assignedDate,
        name: row.source.workoutName,
    };
}
/**
 * Resolves a cross-block row into a prescription-only {@link Workout} (block +
 * athlete layers, no actual). Used for prescription slot rendering so the cell
 * always shows the prescribed values rather than recorded actuals.
 */
function mergeRowToPrescriptionWorkout(row) {
    const blockInput = layerInput(row, 'block', row.layers.block);
    const athleteInput = {
        id: `${row.source.blockId}|${row.source.workoutId}|draft`,
        workoutId: row.source.workoutId,
        layer: row.layers.athlete,
        assignedDate: row.assignedDate,
        name: row.source.workoutName,
    };
    return blockInput
        ? mergeWorkoutLayers(blockInput, athleteInput)
        : mergeWorkoutLayers(athleteInput);
}
/**
 * Resolves a cross-block row into a {@link Workout} that includes the actual
 * layer. Used by the actual slot so it can render recorded values. Returns
 * `undefined` when the row has no actual layer.
 */
function mergeRowToActualWorkout(row) {
    if (!row.layers.actual)
        return undefined;
    const blockInput = layerInput(row, 'block', row.layers.block);
    const athleteInput = {
        id: `${row.source.blockId}|${row.source.workoutId}|draft`,
        workoutId: row.source.workoutId,
        layer: row.layers.athlete,
        assignedDate: row.assignedDate,
        name: row.source.workoutName,
    };
    return mergeWorkoutLayersWithExternal([...BOWLING_SOURCE_RANKING], tagPrescriptionLayer(blockInput ?? athleteInput), blockInput ? tagPrescriptionLayer(athleteInput) : undefined, undefined, layerInput(row, 'actual', row.layers.actual));
}
function rowHasActual(row) {
    return row.layers.actual !== null;
}
/**
 * Stable ordering for sibling sources within a `kind: 'duplicate'` day:
 * same-team rows come before cross-team rows (matches the coach's mental
 * model — "my team first"), then alphabetical by team name and block name
 * so the rendered list is deterministic across reloads.
 */
function compareSources(a, b) {
    if (a.isCrossTeam !== b.isCrossTeam)
        return a.isCrossTeam ? 1 : -1;
    const byTeam = a.teamName.localeCompare(b.teamName);
    if (byTeam !== 0)
        return byTeam;
    return a.blockName.localeCompare(b.blockName);
}
/**
 * Builds the per-(athlete, day) index that drives the cross-block bowling
 * overlay. For each unique day:
 *
 * - Rows whose source block is the current block are dropped (overlap with
 *   yourself is not a conflict).
 * - A single remaining row becomes a `crossBlockDraft` day.
 * - Multiple remaining rows collapse into a `duplicate` day; the primary is
 *   the source with the earliest `workoutCreatedAt` (the block that scheduled
 *   those athletes first has priority). Siblings are sorted via
 *   {@link compareSources} for stable UI ordering.
 *
 * `hasActual` is computed per row and surfaced on every variant so consumers
 * can mirror the current block's `hasActual` gating without re-reading the
 * raw layers.
 */
export function buildBowlingSummaryDays({ currentBlockId, currentTeamId, crossBlockRows, }) {
    const map = new Map();
    const grouped = new Map();
    const seenRowKeys = new Set();
    for (const row of crossBlockRows) {
        if (row.source.blockId === currentBlockId)
            continue;
        const rowKey = dedupRowKey(row);
        if (seenRowKeys.has(rowKey))
            continue;
        seenRowKeys.add(rowKey);
        const key = bowlingSummaryDayKey(row.profileId, row.assignedDate);
        const existing = grouped.get(key);
        if (existing)
            existing.push(row);
        else
            grouped.set(key, [row]);
    }
    for (const [key, rows] of grouped) {
        if (rows.length === 1) {
            const row = rows[0];
            const isCrossTeam = row.source.teamId !== currentTeamId;
            const workout = mergeRowToPrescriptionWorkout(row);
            map.set(key, {
                kind: 'crossBlockDraft',
                source: row.source,
                workout,
                actualWorkout: mergeRowToActualWorkout(row),
                isCrossTeam,
                hasActual: rowHasActual(row),
            });
            continue;
        }
        const sources = rows
            .map((row) => ({
            ...row.source,
            isCrossTeam: row.source.teamId !== currentTeamId,
            workout: mergeRowToPrescriptionWorkout(row),
            actualWorkout: mergeRowToActualWorkout(row),
            hasActual: rowHasActual(row),
        }))
            .sort(compareSources);
        const primary = sources.reduce((earliest, s) => s.workoutCreatedAt < earliest.workoutCreatedAt ? s : earliest);
        map.set(key, { kind: 'duplicate', sources, primary });
    }
    return map;
}
/**
 * Whether the given athlete/day pair has any cross-block bowling overlap
 * recorded in `index`. Used by server-side mutation guards to block writes
 * that would create a same-day conflict.
 *
 * `currentBlockId` is accepted for parity with the server's guard signature
 * but isn't used here — {@link buildBowlingSummaryDays} has already excluded
 * rows whose source equals the current block during index construction.
 */
export function hasBowlingConflict(args, index) {
    void args.currentBlockId;
    const key = bowlingSummaryDayKey(args.profileId, args.assignedDate);
    return index.has(key);
}
//# sourceMappingURL=merge-cross-block-bowling.js.map